import {
  InputChangeEventDetail,
  IonButton,
  IonIcon,
  IonInput,
  useIonToast,
} from '@ionic/react';
import { eyeOutline, eyeOffOutline, lockClosedOutline, warningOutline } from 'ionicons/icons';
import * as yup from 'yup';
import { FC, useRef, useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { onPromise } from '..';
import { Link, useHistory } from 'react-router-dom';
import { IonInputCustomEvent } from '@ionic/core';
import { AuthService } from 'services/auth';
import { toastConfig } from 'config/ToastConfig';
import { useDispatch } from 'react-redux';
import { ReduxCommandCreator } from 'appRedux/actions';
import SmartLoading from 'components/SmartLoading';
import { MobileNumberField } from '../../../components/MobileNumberField';
import { useSupportedPhoneCountries } from 'common/reactHooks';
import {
  formatPhoneNumberForCountry,
  getDefaultSupportedPhoneCountryId,
  getInternationalPhoneNumber,
  getPhoneDisplayValue,
  getPhoneInputMaxLength,
  getSupportedPhoneCountry,
  isPhoneNumberValidForCountry,
  SupportedPhoneCountryId,
} from '../../../../../common/phoneCountries';

interface FormData {
  user: string;
  password: string;
  passwordConfirmation: string;
}

export const create_account_schema = yup.object().shape({
  password: yup
    .string()
    .required('Password is required')
    .matches(
      /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/,
      '8 or more letters, 1 number, a special character (#?!@$%^&*-) and 1 capital letter'
    )
    .min(8, 'Password must include at least 8 characters')
    .max(24, 'Please use a shorter password (max: 24 characters)'),
  passwordConfirmation: yup.string().oneOf([yup.ref('password'), null], 'Passwords must match'),
});

export const SignupAccount: FC = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(create_account_schema),
  });
  const [phone, setPhone] = useState('');
  const [phoneErr, setPhoneErr] = useState('');
  const [selectedCountryId, setSelectedCountryId] = useState<SupportedPhoneCountryId | undefined>();
  const [showPasswordNew, setShowPasswordNew] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isSignupLoading, setSignUpLoading] = useState(false);
  const auth = new AuthService();
  const history = useHistory();
  const [presentToast] = useIonToast();
  const dispatch = useDispatch();
  const commands = ReduxCommandCreator(dispatch);
  const { supportedPhoneCountries: phoneCountryOptions } = useSupportedPhoneCountries();
  const selectedCountry = getSupportedPhoneCountry(selectedCountryId, phoneCountryOptions);

  useEffect(() => {
    const defaultCountryId = getDefaultSupportedPhoneCountryId(phoneCountryOptions);
    const hasSelectedCountry = phoneCountryOptions.some(
      (country) => country.shortLabel === selectedCountryId
    );
    if ((!selectedCountryId || !hasSelectedCountry) && defaultCountryId) {
      setSelectedCountryId(defaultCountryId);
    }
  }, [phoneCountryOptions, selectedCountryId]);

  const onSubmit = (data: FormData) => {
    if (!selectedCountry) {
      setPhoneErr('Mobile country options are loading');
      return;
    }
    if (!phone) {
      setPhoneErr('Cell number is required');
      return;
    } else {
      setPhoneErr('');
    }
    const validatePhNo = isPhoneNumberValidForCountry(phone, selectedCountry);
    if (validatePhNo) {
      setSignUpLoading(true);
      const internationalPhoneNumber = getInternationalPhoneNumber(phone, selectedCountry);
      const phoneDisplayValue = getPhoneDisplayValue(phone, selectedCountry);
      auth.CreateAccount(internationalPhoneNumber, data.password).subscribe({
        next(data) {
          setSignUpLoading(false);
          if (data?.message === 'Success') {
            commands.SetUserInfo(internationalPhoneNumber, phoneDisplayValue);
            history.push('/auth/verifyaccount');
          } else {
            toastConfig.color = 'danger';
            toastConfig.message = data?.errorMsg;
            void presentToast(toastConfig);
          }
        },
        error(err: unknown) {
          const errorMessage = err instanceof Error ? err.message : 'Unable to create account';
          toastConfig.color = 'danger';
          toastConfig.message = errorMessage;
          void presentToast(toastConfig);
          setSignUpLoading(false);
        },
        complete() {
          setSignUpLoading(false);
        },
      });
    } else {
      setPhoneErr(`Please enter a valid ${selectedCountry.label.toLowerCase()} mobile number`);
    }
  };

  const inputEl = useRef<HTMLIonInputElement>(null);

  const formatPhone = (ev: IonInputCustomEvent<InputChangeEventDetail>) => {
    if (!selectedCountry) return;
    const value = `${ev.target.value ?? ''}`;
    const filteredValue = formatPhoneNumberForCountry(value, selectedCountry);
    setPhone(filteredValue);
    if (inputEl.current !== null) inputEl.current.value = filteredValue;
  };

  const onCountryChange = (countryId: SupportedPhoneCountryId) => {
    const nextCountry = getSupportedPhoneCountry(countryId, phoneCountryOptions);
    if (!nextCountry) return;
    setSelectedCountryId(countryId);
    setPhoneErr('');
    const reformattedPhone = formatPhoneNumberForCountry(phone, nextCountry);
    setPhone(reformattedPhone);
    if (inputEl.current !== null) inputEl.current.value = reformattedPhone;
  };

  return (
    <div className="login-v2-form login-v2-form--grow">
      <form
        id="confirm-page"
        onSubmit={onPromise(handleSubmit((data: FormData) => void onSubmit(data)))}
      >
        {/* Phone field */}
        <div className="login-v2-field-wrap login-v2-phone-wrap">
          <div className="login-v2-phone-content">
            <MobileNumberField
              value={phone}
              onChange={formatPhone}
              maxLength={selectedCountry ? getPhoneInputMaxLength(selectedCountry) : 0}
              placeholder="Enter phone number"
              inputRef={inputEl}
              selectedCountryId={selectedCountryId}
              selectedCountryDialCode={selectedCountry?.dialCode}
              onCountryChange={onCountryChange}
              countryOptions={phoneCountryOptions}
            />
          </div>
        </div>
        {phoneErr && (
          <div className="login-v2-field-error">
            <IonIcon icon={warningOutline} />
            <span>{phoneErr}</span>
          </div>
        )}

        {/* Password field */}
        <div className="login-v2-field-wrap login-v2-pass-wrap">
          <IonIcon icon={lockClosedOutline} className="login-v2-field-prefix-icon" />
          <div className="login-v2-pass-content">
            <Controller
              name="password"
              control={control}
              defaultValue=""
              render={({ field: { onChange } }) => (
                <IonInput
                  type={showPasswordNew ? 'text' : 'password'}
                  placeholder="Enter your password"
                  onIonChange={onChange}
                  autocomplete="new-password"
                />
              )}
              rules={{ required: true }}
            />
          </div>
          <div className="login-v2-eye-btn" onClick={() => setShowPasswordNew((v) => !v)}>
            <IonIcon icon={showPasswordNew ? eyeOffOutline : eyeOutline} />
          </div>
        </div>
        {errors.password?.message && (
          <div className="login-v2-field-error">
            <IonIcon icon={warningOutline} />
            <span>{errors.password.message}</span>
          </div>
        )}

        {/* Confirm password field */}
        <div className="login-v2-field-wrap login-v2-pass-wrap">
          <IonIcon icon={lockClosedOutline} className="login-v2-field-prefix-icon" />
          <div className="login-v2-pass-content">
            <Controller
              name="passwordConfirmation"
              control={control}
              defaultValue=""
              render={({ field: { onChange } }) => (
                <IonInput
                  type={showPasswordConfirm ? 'text' : 'password'}
                  placeholder="Confirm password"
                  onIonChange={onChange}
                  autocomplete="new-password"
                />
              )}
              rules={{ required: true }}
            />
          </div>
          <div className="login-v2-eye-btn" onClick={() => setShowPasswordConfirm((v) => !v)}>
            <IonIcon icon={showPasswordConfirm ? eyeOffOutline : eyeOutline} />
          </div>
        </div>
        {errors.passwordConfirmation?.message && (
          <div className="login-v2-field-error">
            <IonIcon icon={warningOutline} />
            <span>{errors.passwordConfirmation.message}</span>
          </div>
        )}

        <IonButton type="submit" expand="block" className="login-v2-submit-btn">
          Create Account
        </IonButton>
      </form>

      <div className="login-v2-form-footer">
        <div className="login-v2-divider" />
        <Link to="/auth/login" className="login-v2-signup-link">
          <button className="login-v2-signup-btn">Log in with existing user</button>
        </Link>
      </div>

      <SmartLoading loading={isSignupLoading} />
    </div>
  );
};
